// Shortens TMDB's free-text `place_of_birth` to a compact label:
//   US-born      -> "City, ST"  (2-letter postal abbreviation)
//   non-US born  -> "City, CC"  (ISO 3166-1 alpha-2 country code)
// TMDB strings are inconsistent (2 or 3+ comma-separated parts, varying country
// spellings), so every branch falls back gracefully and never throws.

const US_STATES = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'district of columbia': 'DC', 'washington dc': 'DC', 'florida': 'FL',
  'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL',
  'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS', 'kentucky': 'KY',
  'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD', 'massachusetts': 'MA',
  'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH',
  'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
  'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', 'tennessee': 'TN',
  'texas': 'TX', 'utah': 'UT', 'vermont': 'VT', 'virginia': 'VA',
  'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI',
  'wyoming': 'WY', 'puerto rico': 'PR', 'guam': 'GU',
}

// US country spellings TMDB uses (normalized: lowercased, periods stripped).
const US_COUNTRY = new Set([
  'usa', 'us', 'united states', 'united states of america', 'america',
])

// Names that are both a US state AND a sovereign country. We only treat these
// as a US state when an explicit US country segment is also present; on their
// own they're left as-is so we never emit a confidently-wrong abbreviation.
const AMBIGUOUS_STATE = new Set(['georgia'])

const COUNTRIES = {
  'united kingdom': 'GB', 'uk': 'GB', 'great britain': 'GB', 'britain': 'GB',
  'england': 'GB', 'scotland': 'GB', 'wales': 'GB', 'northern ireland': 'GB',
  'ireland': 'IE', 'republic of ireland': 'IE',
  'canada': 'CA', 'mexico': 'MX',
  'france': 'FR', 'germany': 'DE', 'west germany': 'DE', 'east germany': 'DE',
  'spain': 'ES', 'italy': 'IT', 'portugal': 'PT',
  'netherlands': 'NL', 'the netherlands': 'NL', 'holland': 'NL',
  'belgium': 'BE', 'switzerland': 'CH', 'austria': 'AT', 'luxembourg': 'LU',
  'sweden': 'SE', 'norway': 'NO', 'denmark': 'DK', 'finland': 'FI',
  'iceland': 'IS', 'poland': 'PL',
  'czech republic': 'CZ', 'czechia': 'CZ', 'czechoslovakia': 'CZ',
  'slovakia': 'SK', 'hungary': 'HU', 'romania': 'RO', 'bulgaria': 'BG',
  'greece': 'GR',
  'russia': 'RU', 'russian federation': 'RU', 'soviet union': 'RU', 'ussr': 'RU',
  'ukraine': 'UA', 'belarus': 'BY',
  'serbia': 'RS', 'yugoslavia': 'RS', 'croatia': 'HR',
  'bosnia and herzegovina': 'BA', 'slovenia': 'SI', 'macedonia': 'MK',
  'albania': 'AL', 'estonia': 'EE', 'latvia': 'LV', 'lithuania': 'LT',
  'turkey': 'TR', 'türkiye': 'TR', 'turkiye': 'TR',
  'australia': 'AU', 'new zealand': 'NZ',
  'japan': 'JP',
  'china': 'CN', "people's republic of china": 'CN', 'peoples republic of china': 'CN',
  'hong kong': 'HK', 'taiwan': 'TW',
  'south korea': 'KR', 'korea': 'KR', 'republic of korea': 'KR', 'north korea': 'KP',
  'india': 'IN', 'pakistan': 'PK', 'bangladesh': 'BD', 'sri lanka': 'LK',
  'thailand': 'TH', 'vietnam': 'VN',
  'philippines': 'PH', 'the philippines': 'PH',
  'indonesia': 'ID', 'malaysia': 'MY', 'singapore': 'SG',
  'israel': 'IL', 'lebanon': 'LB', 'iran': 'IR', 'iraq': 'IQ', 'syria': 'SY',
  'jordan': 'JO', 'egypt': 'EG',
  'united arab emirates': 'AE', 'uae': 'AE', 'saudi arabia': 'SA',
  'south africa': 'ZA', 'nigeria': 'NG', 'kenya': 'KE', 'ghana': 'GH',
  'morocco': 'MA', 'algeria': 'DZ', 'tunisia': 'TN', 'ethiopia': 'ET',
  'brazil': 'BR', 'argentina': 'AR', 'chile': 'CL', 'colombia': 'CO',
  'peru': 'PE', 'venezuela': 'VE', 'ecuador': 'EC', 'uruguay': 'UY',
  'cuba': 'CU', 'puerto rico': 'PR', 'dominican republic': 'DO',
  'jamaica': 'JM', 'trinidad and tobago': 'TT', 'haiti': 'HT',
}

const norm = (s) => s.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim()

/**
 * Format a raw TMDB place_of_birth string. Returns null for empty/unusable
 * input so callers can skip rendering rather than show a blank line.
 */
export function formatPlaceOfBirth(raw) {
  if (!raw || typeof raw !== 'string') return null

  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean)
  if (parts.length === 0) return null

  const city = parts[0]
  if (parts.length === 1) return city

  const last = parts[parts.length - 1]
  const lastNorm = norm(last)

  // ── US: explicit country segment, or a bare trailing state (country omitted) ──
  const endsInUsCountry = US_COUNTRY.has(lastNorm)
  const endsInBareState = US_STATES[lastNorm] != null && !AMBIGUOUS_STATE.has(lastNorm)

  if (endsInUsCountry || endsInBareState) {
    // The state is the trailing segment itself, or the part just before the
    // country segment (handles a stray county/region in the middle).
    const stateRaw = endsInBareState ? last : parts[parts.length - 2]
    const code = stateRaw ? US_STATES[norm(stateRaw)] : null
    if (code) return `${city}, ${code}`
    // US but state unknown/missing — show city + a distinct region as-is, else city alone.
    if (stateRaw && norm(stateRaw) !== norm(city)) return `${city}, ${stateRaw}`
    return city
  }

  // ── Non-US ──
  const code = COUNTRIES[lastNorm]
  if (code) return `${city}, ${code}`

  // Unknown country — keep the city and the trailing segment as-is.
  return `${city}, ${last}`
}
